/**
 * StudioShell — the 8-stage workbench chrome (§5f).
 *
 * Pure layout: the session header, the stage spine, and the two zones (left
 * programme navigator, centre active stage). The
 * orchestrator (ResearchStudio) owns all state and hands the three zones in as
 * nodes plus the stage descriptors; the shell knows nothing about the engine.
 * Palette/type are the fixed prototype design (MASTER_PLAN §5f).
 */
import type { ReactNode } from 'react'
import type { StageDef, StageKey } from './shellTypes'

export interface StudioShellProps {
  // session (on the stage bar, after the last stage)
  sessionName: string
  status: string
  scope: string
  savedLabel: string
  onOpen: () => void
  onNew: () => void
  onSave: () => void
  saving?: boolean
  saveDisabled?: boolean
  /** The season build — the LAST thing on the stage bar (Chris, 2026-10-28). */
  buildAction?: ReactNode
  /** ⚠ Interventions — straight after the last stage, only when needed. */
  interventions?: ReactNode

  // spine
  stages: StageDef[]
  activeStage: StageKey
  onSelectStage: (k: StageKey) => void

  // zones — programme is optional: the Season programme moved to a card on the
  // Calendar stage (Chris, 2026-08-20: it stole rail space all session while
  // only ever showing the active slice); pass null to drop the left aside.
  programme?: ReactNode
  /** Optional banner (errors / empty-data notices) shown above the stage body. */
  banner?: ReactNode
  children: ReactNode
}

export function StudioShell(p: StudioShellProps) {
  return (
    <section className="stu stu-shell">
      {/* ---- stage spine + session --------------------------------------
          The green "Timetable Studio" banner is gone (Chris, 2026-10-28: "we
          already know we are in the Timetable Studio as we clicked on the
          tab"). Its session controls ride the stage bar after Results, and the
          season build sits at the very end. No stage title or blurb either —
          the bar already names the stage. */}
      <nav className="sh-spine">
        {p.stages.map((s) => {
          const active = s.key === p.activeStage
          const mark = s.complete && !active ? '✓' : String(s.n)
          const badgeCls = active
            ? 'sh-stage-badge'
            : s.complete ? 'sh-stage-badge sh-stage-badge--done' : 'sh-stage-badge'
          return (
            <button
              key={s.key}
              type="button"
              className={`sh-stage${active ? ' sh-stage--active' : ''}`}
              onClick={() => p.onSelectStage(s.key)}
            >
              <span className={badgeCls}>{mark}</span>
              <span className="sh-stage-text">
                <span className="sh-stage-n">Stage {s.n}</span>
                <span className="sh-stage-label">{s.label}</span>
              </span>
            </button>
          )
        })}
        {p.interventions}
        <div className="sh-spine-sess">
          <button type="button" className="sh-sess-pick" onClick={p.onOpen} title="Open, rename or delete a session">
            <span className="sh-sess-row">
              <span className="sh-sess-name">{p.sessionName}</span>
              <span className="sh-status">{p.status}</span>
            </span>
            <span className="sh-scope">{p.scope} · {p.savedLabel}</span>
          </button>
          <button type="button" className="sh-sbtn" onClick={p.onOpen}>Open…</button>
          <button type="button" className="sh-sbtn" onClick={p.onNew}>New session</button>
          <button type="button" className="sh-sbtn sh-sbtn--gold" onClick={p.onSave} disabled={p.saving || p.saveDisabled}>
            {p.saving ? 'Saving…' : 'Save'}
          </button>
          {p.buildAction}
        </div>
      </nav>

      {/* ---- body : three zones ---- */}
      <div className={`sh-body${p.programme ? '' : ' sh-body--noleft'}`}>
        {p.programme ? <aside className="sh-left">{p.programme}</aside> : null}

        <main className="sh-center">
          {p.banner}
          {p.children}
        </main>
        {/* The Live results rail was removed (Chris, 2026-09-04: "it's useless
            and in the way all the time"). The money lives on the Results stage. */}
      </div>
    </section>
  )
}
